import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import Area from '@/components/apps/layouts/shared/Area';
import { IndicatorItemType } from '@/components/apps/ui/indicator/IndicatorItem';

import SolutionContent from '../shared/architecture/SolutionContent';

export default function ComponentsSection() {
  const { t } = useTranslation('common');

  const indicatorItems: IndicatorItemType[] = [
    {
      text: 'Listen AI Edge',
      href: '#components-1',
    },
    {
      text: t('Listen AI CMS'),
      href: '#components-2',
    },
    {
      text: t('마이크'),
      href: '#components-3',
    },
  ];

  const solutionItems = [
    {
      title: 'Listen AI Edge',
      description: t(
        'Listen AI Edge는 올인원 소리 분석 장치로 Listen AI 분석 엔진, PoE 등 전원/네트워킹 모듈, 고성능 마이크 연결 포트 등이 모두 포함되어 있습니다. 소리를 분석하려는 공간에 엣지를 설치하면 간단하게 Listen AI 솔루션을 사용할 수 있습니다.',
      ),
      image: {
        mobile: {
          src: {
            ko: '/images/apps/technology/sec_05_img_01_m.png',
          },
          height: 1095,
        },
        desktop: {
          src: {
            ko: '/images/apps/technology/sec_05_img_01.png',
          },
          height: 360,
        },
      },
      infoItems: [
        {
          title: 'CPU',
          description: 'Up to 2.4 GHz (8-core) 64-bit',
        },
        {
          title: 'RAM',
          description: '16 GB',
        },
        {
          title: 'Storage',
          description: '128 GB',
        },
        {
          title: 'Size',
          description: '120×83×55 (mm)',
        },
        {
          title: 'Connectivity',
          description: '2 x PCle 2.5G LAN',
        },
      ],
      feature: {
        title: t('주요 특징'),
        items: [
          {
            title: t('실시간 AI 오디오 분석'),
            description: t(
              '최대 4개 마이크 연결을 지원하며. 오디오 분석 AI엔진이 내부에 포함되어 있는 장비로 실시간으로 수집된 소리를 분석하고 이상 음원을 판별해 대시보드로 알람을 보낼 수 있습니다.',
            ),
          },
          {
            title: t('개인정보보호 모드'),
            description: t(
              '수집된 소리를 엣지의 AI 내부 엔진으로 분석하고 결과만 서버로 보내는 방식으로 구성이 가능해 개인정보가 포함된 음원 클립이 유출되지 않는 안전한 솔루션 구축이 가능합니다.',
            ),
          },
          {
            title: t('PoE 방식 지원'),
            description: t(
              'Ethernet (PoE) 방식의 네트워크 연결방식을 지원해 다양한 상황에 적용 가능합니다.',
            ),
          },
        ],
      },
    },
    {
      title: 'Listen AI Monitoring System',
      description: t(
        'Listen AI 솔루션 분석 결과를 실시간으로 확인할 수 있는 웹 기반 통합 모니터링 관리 콘솔입니다.',
      ),
      image: {
        mobile: {
          src: {
            // TODO: 모바일 이미지로 교체
            ko: '/images/apps/technology/sec_05_img_02.png',
            en: '/images/apps/technology/sec_05_img_02_m_en.png',
          },
          height: 681,
        },
        desktop: {
          src: {
            ko: '/images/apps/technology/sec_05_img_02.png',
            en: '/images/apps/technology/sec_05_img_02_en.png',
          },
          height: 266,
        },
      },
      feature: {
        title: t('주요 특징'),
        items: [
          {
            title: t('실시간 알람 및 위치 확인'),
            description: t(
              '관제실에 제공되는 모니터링 대시보드로, 실시간으로 이상 음원을 감지했을 때 즉각 알람을 전송하고 이상 음원 발생 위치 정보를 제공해 신속하고 정확한 대응을 돕습니다.',
            ),
          },
          {
            title: t('데이터 분석 결과 리포트 및 관리'),
            description: t(
              '감지된 소리의 정확도와 히스토리 관리가 가능하며, 이상 음원의 데이터 분석 결과를 리포트를 통해 유용한 정보를 제공합니다.',
            ),
          },
          {
            title: t('기존 모니터링 시스템과 연동'),
            description: t(
              '기존에 사용하던 영상 모니터링 시스템 VMS 연동이 가능해 더욱 유연한 모니터링 환경을 구축할 수 있습니다. REST API, Webhook, SIP 등을 지원합니다.',
            ),
          },
        ],
      },
    },
    {
      title: t('마이크'),
      description: t(
        'Listen AI Edge에 연결되는 마이크입니다. 인공지능 분석 전용의 고성능 마이크로 실내/외 환경에 맞춘 마이크 3종을 선택할 수 있습니다. 엣지서버를 통해 전원/네트워크를 공급합니다.',
      ),
      mike: [
        {
          title: t('실내용 A'),
          mobileImageSrc: '/images/apps/technology/sec_05_img_04_m.png',
          desktopImageSrc: '/images/apps/technology/sec_05_img_04.png',
          items: [
            {
              title: t('수용 범위'),
              description: t('최대 30m'),
            },
            {
              title: t('지향성'),
              description: 'Omni + Directional',
            },
            {
              title: t('주파수 응답'),
              description: '20Hz - 20kHz',
            },
            {
              title: t('크기'),
              description: '113×42×37 (mm)',
            },
            {
              title: t('전원/네트워크'),
              description: 'PoE (IEEE802.3at/af)',
            },
          ],
        },
        {
          title: t('실내용 B'),
          mobileImageSrc: '/images/apps/technology/sec_05_img_03_m.png',
          desktopImageSrc: '/images/apps/technology/sec_05_img_03.png',
          items: [
            {
              title: t('수용 범위'),
              description: t('최대 180m²'),
            },
            {
              title: t('지향성'),
              description: 'Omnidirection',
            },
            {
              title: t('주파수 응답'),
              description: '20Hz - 20kHz',
            },
            {
              title: t('크기'),
              description: 'φ85×18 ㏙m',
            },
            {
              title: t('전원/네트워크'),
              description: 'PoE (IEEE802.3at/af)',
            },
          ],
        },
        {
          title: t('실외용'),
          mobileImageSrc: '/images/apps/technology/sec_05_img_05_m.png',
          desktopImageSrc: '/images/apps/technology/sec_05_img_05.png',
          items: [
            {
              title: t('수용 범위'),
              description: t('최대 30m'),
            },
            {
              title: t('지향성'),
              description: 'Omni + Directional',
            },
            {
              title: t('주파수 응답'),
              description: '20Hz - 20kHz',
            },
            {
              title: t('크기'),
              description: '188×75×32 (mm)',
            },
            {
              title: t('전원/네트워크'),
              description: 'PoE (IEEE802.3at/af)',
            },
          ],
        },
      ],
    },
  ];

  return (
    <section className="bg-[linear-gradient(180deg,#f6f6f7_0%,rgba(246,246,247,0)_20%)] py-[100px] lg:py-[clamp(100px,16.667vw,200px)]">
      <Area>
        <SolutionContent
          title={t('솔루션 구성요소')}
          idPrefix="components"
          indicatorItems={indicatorItems}
          solutionItems={solutionItems}
        />
      </Area>
    </section>
  );
}
